import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { getPaidSessionId } from '../paid/session';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ScrollArea } from '../components/ui/scroll-area';
import { Send, Trash2, BookOpen, Loader2, Copy, Check } from 'lucide-react';
import { toast } from 'sonner';
import MarkdownRenderer from '../components/MarkdownRenderer';

const MODULE_INFO = {
  module1: {
    name: 'Module 1',
    title: 'Baseline & Setup',
    description: 'Equipment check, technique foundations, baseline test, and personalized plan generation.',
    welcomeMessage: "Welcome to Module 1 — Baseline & Setup. I'm Coach Rands. Let's build your foundation for adding 20kg to your weighted pull-up.\n\nBefore we begin, I need to understand your equipment setup. This ensures your program is tailored to what you have access to.\n\nFirst question: What loading equipment do you have?\n• Belt\n• Vest\n• Dumbbell\n• Backpack\n• None"
  },
  module2: {
    name: 'Module 2',
    title: 'Programming & Load Management',
    description: 'RPE calibration, progression rules, session logging, and plateau protocols.',
    welcomeMessage: "Welcome to Module 2 — Programming and Load Management.\n\nThis is where we put your plan into action. Over the course of your 12 weeks together here is what this module handles:\n\n• Session logging after every workout so I can track your progress and tell you exactly when to add load and when to hold\n• RPE calibration so we know your effort is being measured accurately\n• Progression decisions — when to add 2.5kg, when to hold, and what to do when progress stalls\n• Outside volume monitoring so we can catch recovery issues before they become plateaus\n• Phase guidance so you always know what the current block of training is trying to achieve\n\nEvery session you log here I will give you specific feedback — not generic encouragement. Real coaching based on your actual numbers.\n\nTo get started, paste your coaching profile below. You generated this at the end of Module 1. It tells me everything I need to pick up exactly where we left off."
  },
  module3: {
    name: 'Module 3',
    title: 'Mindset & Expectation Management',
    description: 'Motivation anchors, dropout prevention, and mental frameworks for long-term progress.',
    welcomeMessage: "Welcome to Module 3.\n\nThis is the part of the program most people underestimate.\n\nThe training is the easy part. The hard part is showing up consistently when:\n\n1. Life gets in the way\n2. Progress stalls\n3. The novelty wears off\n\nThat is where most people quietly fall apart.\n\nThis module is built to make sure that does not happen to you.\n\nBefore we start, paste your coaching profile from Module 1 so I can add your motivation anchor to it when we are done."
  },
  module4: {
    name: 'Module 4',
    title: 'Joint Pain & Constraints',
    description: 'Pain management protocols and training modifications.',
    welcomeMessage: "Welcome to Module 4 — Joint Pain & Constraints.\n\nThis module is coming soon. The full pain protocol and constraint management system is being developed.\n\nFor now, I can provide brief guidance on common issues. Paste your coaching profile here and tell me what you're experiencing."
  },
  module5: {
    name: 'Module 5',
    title: 'Accessory Work',
    description: 'Supplemental exercises to support pull-up strength.',
    welcomeMessage: "Welcome to Module 5 — Accessory Work.\n\nThis module is coming soon. The full accessory programming system is being developed.\n\nThis will cover horizontal rows, face pulls, grip training, and other exercises that directly support your weighted pull-up progress."
  },
  module6: {
    name: 'Module 6',
    title: 'Load Management & Equipment',
    description: 'Rep cycling protocol and strength ceiling breakthroughs.',
    welcomeMessage: "Paste your coaching profile here to continue."
  },
  reference: {
    name: 'Reference',
    title: 'Technique Deep Dive',
    description: 'Comprehensive technique breakdowns, drill prescriptions, and troubleshooting.',
    welcomeMessage: "Welcome to the Reference Module — Technique Deep Dive.\n\nThis is your permanent resource for technique mastery. Are you here for the full technique breakdown, or is something specific breaking down?"
  }
};

const ModulePage = ({ moduleId }) => {
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [copyStatus, setCopyStatus] = useState({});
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const navigate = useNavigate();
  const sessionId = getPaidSessionId();
  
  const moduleInfo = MODULE_INFO[moduleId];

  useEffect(() => {
    loadConversation();
  }, [moduleId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadConversation = async () => {
    try {
      const response = await axios.get(`${API}/conversation/${sessionId}/${moduleId}`);
      if (response.data.messages && response.data.messages.length > 0) {
        setMessages(response.data.messages);
      } else {
        // Show welcome message
        setMessages([{
          role: 'assistant',
          content: moduleInfo.welcomeMessage,
          timestamp: new Date().toISOString()
        }]);
      }
    } catch (error) {
      console.error('Error loading conversation:', error);
      setMessages([{
        role: 'assistant',
        content: moduleInfo.welcomeMessage,
        timestamp: new Date().toISOString()
      }]);
    }
  };

  const sendMessage = async (e) => {
    e?.preventDefault();
    if (!inputValue.trim() || isLoading) return;

    const userMessage = {
      role: 'user',
      content: inputValue.trim(),
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);

    try {
      const response = await axios.post(`${API}/chat`, {
        session_id: sessionId,
        module: moduleId,
        message: userMessage.content
      });

      const assistantMessage = {
        role: 'assistant',
        content: response.data.response,
        timestamp: response.data.timestamp
      };

      setMessages(prev => [...prev, assistantMessage]);
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message. Please try again.');
      // Remove the user message if failed
      setMessages(prev => prev.slice(0, -1));
      setInputValue(userMessage.content);
    } finally {
      setIsLoading(false);
      inputRef.current?.focus();
    }
  };

  const clearConversation = async () => {
    try {
      await axios.delete(`${API}/conversation/${sessionId}/${moduleId}`);
      setMessages([{
        role: 'assistant',
        content: moduleInfo.welcomeMessage,
        timestamp: new Date().toISOString()
      }]);
      toast.success('Conversation cleared');
    } catch (error) {
      console.error('Error clearing conversation:', error);
      toast.error('Failed to clear conversation');
    }
  };

  const copyToClipboard = async (text, index) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopyStatus(prev => ({ ...prev, [index]: 'Copied!' }));
      setTimeout(() => setCopyStatus(prev => ({ ...prev, [index]: null })), 2000);
    } catch (err) {
      // Fallback for browsers that block clipboard API
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.opacity = '0';
      textArea.style.left = '-9999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      try {
        document.execCommand('copy');
        setCopyStatus(prev => ({ ...prev, [index]: 'Copied!' }));
        setTimeout(() => setCopyStatus(prev => ({ ...prev, [index]: null })), 2000);
      } catch (fallbackErr) {
        setCopyStatus(prev => ({ ...prev, [index]: 'Please copy manually' }));
        setTimeout(() => setCopyStatus(prev => ({ ...prev, [index]: null })), 3000);
      }
      document.body.removeChild(textArea);
    }
  };

  const renderMessage = (message, index) => {
    const isUser = message.role === 'user';
    const isProfile = message.content.includes('YOUR COACHING PROFILE') || 
                      message.content.includes('========================================');
    
    return (
      <div 
        key={index}
        className={`flex ${isUser ? 'justify-end' : 'justify-start'} animate-fade-in-up`}
        data-testid={`chat-message-${index}`}
      >
        <div className={`relative group ${isUser ? 'max-w-[85%]' : 'max-w-[85%]'}`}>
          {/* Avatar for Coach */}
          {!isUser && (
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-none bg-crimson flex items-center justify-center flex-shrink-0 mt-1">
                <span className="font-heading font-bold text-white text-sm">CR</span>
              </div>
              <div 
                className={`${isProfile ? 'profile-block p-4 text-sm' : 'chat-message-coach p-4'} text-white`}
              >
                {isProfile ? (
                  <div className="whitespace-pre-wrap font-mono text-sm leading-relaxed">
                    {message.content}
                  </div>
                ) : (
                  <MarkdownRenderer 
                    content={message.content} 
                    className="font-body text-sm md:text-base leading-relaxed"
                  />
                )}
                {/* Copy button for profile */}
                {isProfile && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => copyToClipboard(message.content, index)}
                    className={`mt-3 ${
                      copyStatus[index] === 'Copied!' 
                        ? 'bg-crimson/20 text-crimson' 
                        : copyStatus[index] === 'Please copy manually'
                        ? 'text-crimson'
                        : 'text-mono-mute hover:text-crimson'
                    }`}
                    data-testid={`copy-profile-btn-${index}`}
                  >
                    {copyStatus[index] === 'Copied!' ? (
                      <><Check className="h-4 w-4 mr-2" /> Copied!</>
                    ) : copyStatus[index] === 'Please copy manually' ? (
                      <><Copy className="h-4 w-4 mr-2" /> Please copy manually</>
                    ) : (
                      <><Copy className="h-4 w-4 mr-2" /> Copy Profile</>
                    )}
                  </Button>
                )}
              </div>
            </div>
          )}
          
          {/* User message */}
          {isUser && (
            <div className="chat-message-user p-4 text-white">
              <div className="whitespace-pre-wrap font-body text-sm md:text-base leading-relaxed">
                {message.content}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="flex flex-col h-screen bg-mono-base" data-testid={`module-page-${moduleId}`}>
      {/* Header */}
      <header className="border-b border-mono-line bg-mono-card/50 backdrop-blur-sm px-4 md:px-8 py-4 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-3">
              <span className="text-crimson font-heading text-xs font-bold uppercase tracking-widest">
                {moduleInfo.name}
              </span>
              {moduleId === 'module4' && (
                <span className="border border-crimson text-crimson text-xs px-2 py-0.5 rounded-none font-bold uppercase">
                  Coming Soon
                </span>
              )}
            </div>
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-white uppercase tracking-tight mt-1">
              {moduleInfo.title}
            </h1>
            <p className="text-mono-mute text-sm mt-1 hidden md:block">
              {moduleInfo.description}
            </p>
          </div>
          
          <div className="flex items-center gap-2">
            {moduleId !== 'reference' && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/reference')}
                className="text-mono-mute hover:text-crimson"
                data-testid="reference-btn"
              >
                <BookOpen className="h-4 w-4 mr-2" />
                <span className="hidden md:inline">Reference</span>
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={clearConversation}
              className="text-mono-mute hover:text-crimson"
              data-testid="clear-conversation-btn"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              <span className="hidden md:inline">Clear</span>
            </Button>
          </div>
        </div>
      </header>

      {/* Messages Area */}
      <ScrollArea className="flex-1 px-4 md:px-8 py-6">
        <div className="max-w-3xl mx-auto space-y-6">
          {messages.map((message, index) => renderMessage(message, index))}
          
          {/* Typing Indicator */}
          {isLoading && (
            <div className="flex justify-start animate-fade-in-up">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-none bg-crimson flex items-center justify-center flex-shrink-0 mt-1">
                  <span className="font-heading font-bold text-white text-sm">CR</span>
                </div>
                <div className="chat-message-coach p-4">
                  <div className="flex gap-1.5">
                    <div className="typing-dot"></div>
                    <div className="typing-dot"></div>
                    <div className="typing-dot"></div>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>
      </ScrollArea>

      {/* Input Area */}
      <div className="border-t border-mono-line bg-mono-card/50 backdrop-blur-sm px-4 md:px-8 py-4 flex-shrink-0">
        <form onSubmit={sendMessage} className="max-w-3xl mx-auto">
          <div className="flex gap-3">
            <Input
              ref={inputRef}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              placeholder="Type your message..."
              disabled={isLoading}
              className="flex-1 bg-mono-base border-mono-line text-white placeholder:text-mono-mute focus:border-crimson focus:ring-1 focus:ring-crimson rounded-none h-12 font-body"
              data-testid="chat-input"
            />
            <Button
              type="submit"
              disabled={!inputValue.trim() || isLoading}
              className="bg-white hover:bg-[#E5E5E5] text-black font-bold rounded-none h-12 px-6 transition-colors active:scale-95 disabled:opacity-50"
              data-testid="send-message-btn"
            >
              {isLoading ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                <Send className="h-5 w-5" />
              )}
            </Button>
          </div>
          <p className="text-mono-mute text-xs mt-2 text-center">
            Video submissions: coachrands1@gmail.com • Reviewed within 48 hours
          </p>
        </form>
      </div>
    </div>
  );
};

export default ModulePage;
